/**
 * The auditor export: every unreconciled ledger entry up to the end of a chosen day, as a
 * CSV file, not capped at a screen page. Manager / super_admin only; each export is
 * recorded in the audit log by the server.
 *
 * The file is fetched through the authenticated axios instance (a plain link would
 * carry no bearer token) and saved from the blob.
 */
import { useState } from 'react';
import reconciliationService from '../../services/reconciliation.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { describeExport, exportProblem, isManagerRole, todayUtc } from './bankRecon.helpers';

const inputStyle = { background: colors.canvas, color: colors.ink, border: `1px solid ${colors.border}` };

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReconciliationExport(): JSX.Element | null {
  const role = authService.getRole();
  const [asOf, setAsOf] = useState('');
  const [includeDisputed, setIncludeDisputed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ text: string; warn: boolean } | null>(null);

  if (!isManagerRole(role)) return null;

  const today = todayUtc();
  const problem = exportProblem(asOf, today);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setOutcome(null);
    try {
      const file = await reconciliationService.exportPendingCsv({
        includeDisputed,
        asOf: asOf === '' ? undefined : asOf,
      });
      saveBlob(file.blob, file.filename);
      setOutcome(describeExport(file));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not export the report.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ExportView
      asOf={asOf} today={today} includeDisputed={includeDisputed} busy={busy}
      error={error} outcome={outcome} problem={problem}
      onAsOf={setAsOf} onIncludeDisputed={setIncludeDisputed} onRun={() => { void run(); }}
    />
  );
}

export function ExportView({
  asOf, today, includeDisputed, busy, error, outcome, problem, onAsOf, onIncludeDisputed, onRun,
}: Readonly<{
  asOf: string;
  today: string;
  includeDisputed: boolean;
  busy: boolean;
  error: string | null;
  outcome: { text: string; warn: boolean } | null;
  problem: string | null;
  onAsOf: (value: string) => void;
  onIncludeDisputed: (value: boolean) => void;
  onRun: () => void;
}>): JSX.Element {
  return (
    <section className="rounded-2xl p-4 mt-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: colors.inkSoft }}>
        Auditor export (CSV)
      </h2>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        Every ledger entry still awaiting bank evidence up to the end of the chosen day (UTC),
        not limited to what this page shows. The counts it reports are as of now. Each export
        is recorded with who ran it.
      </p>
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {outcome && (
        <output className="block rounded-lg p-2 mb-2 text-xs"
                style={outcome.warn
                  ? { background: colors.warningBg, color: colors.warning, border: `1px solid ${colors.warning}` }
                  : { background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {outcome.text}
        </output>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="export-as-of" className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            As of (leave empty for today)
          </label>
          <input id="export-as-of" type="date" value={asOf} max={today} disabled={busy}
                 onChange={(e) => onAsOf(e.target.value)}
                 className="px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        </div>
        <label htmlFor="export-disputed" className="flex items-center gap-2 text-sm pb-2"
               style={{ color: colors.ink }}>
          <input id="export-disputed" type="checkbox" checked={includeDisputed} disabled={busy}
                 onChange={(e) => onIncludeDisputed(e.target.checked)} />
          <span>Include disputed entries</span>
        </label>
        <button type="button" onClick={onRun} disabled={busy || problem !== null}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ background: colors.navy, color: '#FFFFFF' }}>
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      {problem && <p className="text-xs mt-2" style={{ color: colors.warning }}>{problem}</p>}
    </section>
  );
}
